// §HUB — the creator-facing Integration Hub. The searchable catalog of what Pomela can connect to.
//
// WHY THIS EXISTS. The catalog and its API (`GET /v1/connectors`) shipped without any UI, so the
// only way to discover a connector was to call the endpoint by hand. A catalog nobody can browse is
// not a catalog — this is the surface a creator actually uses.
//
// THE HONESTY RULE THIS SCREEN ENFORCES. Every connector carries obligations — attribution,
// share-alike, "the free tier is non-commercial". Those are computed SERVER-SIDE
// (`connectorObligations`) precisely so a UI cannot forget them, and this screen renders them
// inline on every card rather than hiding them behind a detail click. The commercial-use status is
// shown as a coloured chip on the card face, because "can I charge for a product built on this?"
// is the question a creator must not have to dig for.
import { useEffect, useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Ic } from './icons';

interface Connector {
  id: string; name: string; domain: string; transport: string; summary: string;
  auth: string; license: string; commercialUse: string; attribution: string;
  freeQuota: string; rateLimit: string; freshness: string; ttlSeconds: number;
  cost: string; fallback: string; docsUrl: string; tags: string[];
  obligations: string[];
  /** Derived server-side from the engine's live-API allowlist — never hand-set. */
  availability: 'buildable_now' | 'directory_only';
  origin: string;
}

type Load =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; connectors: Connector[]; total: number; buildableNow: number; domains: string[] };

const DOMAIN_LABEL: Record<string, string> = {
  weather_environment: 'Weather & environment',
  maps_places: 'Maps & places',
  economics_finance: 'Economics & finance',
  knowledge_reference: 'Knowledge & reference',
  transit_calendars: 'Transit & calendars',
  open_government: 'Open government',
  content_media: 'Content & media',
  business_tools: 'Business tools',
};

/** The commercial-use chip. Wording is the creator's actual answer, not the enum name. */
const COMMERCIAL: Record<string, { label: string; tone: 'ok' | 'warn' | 'info' }> = {
  permitted: { label: 'Commercial use OK', tone: 'ok' },
  permitted_with_conditions: { label: 'Commercial, with conditions', tone: 'info' },
  paid_plan_required: { label: 'Paid plan for commercial', tone: 'warn' },
  varies_by_source: { label: 'Licence depends on source', tone: 'info' },
  review_required: { label: 'Check provider terms', tone: 'warn' },
};

const AUTH_LABEL: Record<string, string> = {
  none: 'No key needed', api_key: 'API key', oauth2: 'Sign in (OAuth)', publisher_url: 'You supply the URL',
};

export function ConnectorsV2(): JSX.Element {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [q, setQ] = useState('');
  const [domain, setDomain] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch('/v1/connectors', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`http ${r.status}`))))
      .then((j: { connectors?: Connector[]; total?: number; buildableNow?: number; domains?: string[] }) => {
        if (!live) return;
        setLoad({
          status: 'ready', connectors: j.connectors ?? [], total: j.total ?? 0,
          buildableNow: j.buildableNow ?? 0, domains: j.domains ?? [],
        });
      })
      .catch((e: Error) => { if (live) setLoad({ status: 'error', message: e.message }); });
    return () => { live = false; };
  }, []);

  // Filtering is client-side over the full catalog: it is 25 rows, so a round trip per keystroke
  // would be slower and would make the screen useless offline. The server-side search endpoint
  // exists for the generation model, which does not have the list in hand.
  const visible = useMemo(() => {
    if (load.status !== 'ready') return [];
    const needle = q.trim().toLowerCase();
    return load.connectors.filter((c) => {
      if (domain && c.domain !== domain) return false;
      if (!needle) return true;
      const hay = `${c.name} ${c.summary} ${c.domain} ${c.tags.join(' ')}`.toLowerCase();
      return needle.split(/\s+/).every((term) => hay.includes(term));
    });
  }, [load, q, domain]);

  if (load.status === 'loading') {
    return <div class="wrap" data-connectors="loading"><p class="muted">Loading connectors…</p></div>;
  }
  if (load.status === 'error') {
    return (
      <div class="wrap" data-connectors="error">
        <h1>Integrations</h1>
        <p class="muted">Couldn’t load the connector catalog ({load.message}). Nothing is connected — try again.</p>
      </div>
    );
  }

  return (
    <div class="wrap" data-connectors="ready">
      <header style={{ marginBottom: 18 }}>
        <h1 data-connectors-title>Data sources</h1>
        {/* The headline states BOTH numbers. "25 connectors" alone reads as "25 things you can
            build with"; only the engine-allowlisted ones are, and the rest are a reference list. */}
        {/* §REFERENCE-DIRECTORY — what this screen may honestly claim.
            An audit found the server-side connector runtime has zero callers anywhere in the
            product: no generated app, no worker and no creator screen invokes configure / data /
            status / revoke, and production holds zero configured connectors and zero stored
            connector keys. So Pomela does NOT manage these integrations — there is no server-held
            key, no enforced refresh, no failure log and no revoke behind them.
            What the "allowlisted" 9 actually get is permission for a generated app to fetch that
            origin directly from the browser. That is a real and useful capability, and it is a
            categorically smaller promise than "integration". The copy says which one it is. */}
        <p class="muted" data-connectors-count>
          A reference directory of <b>{load.total}</b> data sources across {load.domains.length} domains.
          <b data-connectors-buildable>{load.buildableNow}</b> can be read directly by an app you
          build; the other <b>{load.total - load.buildableNow}</b> are listed so you can judge
          licence and cost before you commit to one.
          Every entry states its licence, what it costs, and what you must credit.
        </p>
        <p class="muted" data-connectors-managed="false" style={{ marginTop: 6, fontSize: 13 }}>
          Pomela doesn’t manage these connections for you yet — there’s no stored API key, scheduled
          refresh or delivery monitoring behind them. Your app reads the source directly.
        </p>
      </header>

      {/* §PLACEHOLDER-CLIP — the field defaulted to its intrinsic width, which clipped the
          placeholder mid-word ("Search integrations — ↑"), hiding the three example queries the
          copy relies on to teach people what to search for. Copy nobody can read is copy that
          isn't there. */}
      <div class="field" style={{ marginBottom: 12, width: '100%' }}>
        <input
          style={{ width: '100%' }}
          type="search"
          data-connectors-search
          placeholder="Search integrations — try “exchange rate”, “bus times”, “weather”"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          aria-label="Search integrations"
        />
      </div>

      <div class="chips" role="group" aria-label="Filter by domain" style={{ marginBottom: 18 }}>
        <button
          class={`chip${domain === '' ? ' on' : ''}`} data-domain-chip="" aria-pressed={domain === ''}
          onClick={() => setDomain('')}
        >All</button>
        {load.domains.map((d) => (
          <button
            key={d} class={`chip${domain === d ? ' on' : ''}`} data-domain-chip={d} aria-pressed={domain === d}
            onClick={() => setDomain(d)}
          >{DOMAIN_LABEL[d] ?? d}</button>
        ))}
      </div>

      <p class="muted" data-connectors-matched style={{ marginBottom: 10 }}>
        {visible.length} of {load.total} shown
      </p>

      {visible.length === 0 ? (
        // Honest empty state: names what was searched, offers no fabricated suggestion.
        <div class="card" data-connectors-empty style={{ padding: 20 }}>
          <b>No integration matches “{q}”.</b>
          <p class="muted" style={{ margin: '6px 0 0' }}>
            Pomela connects {load.total} named sources today. If yours isn’t here, a generic REST,
            RSS, iCalendar or CSV connector may still fit — clear the search to browse them.
          </p>
        </div>
      ) : (
        <div class="grid" data-connectors-list>
          {visible.map((c) => {
            const com = COMMERCIAL[c.commercialUse] ?? { label: c.commercialUse, tone: 'info' as const };
            const open = openId === c.id;
            return (
              <article key={c.id} class="card" data-connector={c.id} style={{ padding: 16 }}>
                <div class="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <b style={{ fontSize: 15 }} data-connector-name>{c.name}</b>
                  <span class={`badge ${com.tone}`} data-connector-commercial={c.commercialUse}>{com.label}</span>
                </div>
                <p class="muted" style={{ margin: '6px 0 10px', fontSize: 13.5 }}>{c.summary}</p>

                <div class="wrap-row" style={{ gap: 6, marginBottom: 10 }}>
                  {/* Availability first. This badge was missing entirely, and its absence read as
                      "all 25 are connectable" — only the engine-allowlisted ones are. */}
                  <span
                    class={`badge ${c.availability === 'buildable_now' ? 'ok' : ''}`}
                    data-connector-availability={c.availability}
                  >{c.availability === 'buildable_now' ? 'App can read this' : 'Reference only'}</span>
                  <span class="badge">{DOMAIN_LABEL[c.domain] ?? c.domain}</span>
                  <span class="badge" data-connector-auth={c.auth}>{AUTH_LABEL[c.auth] ?? c.auth}</span>
                  {/* Was "Refresh every 1 h", which implied Pomela enforces that interval. It does
                      not — ttlSeconds is guidance for picking a source, and the generated app sets
                      its own polling. The wording now says what is true. */}
                  <span class="badge">Suggested refresh {formatTtl(c.ttlSeconds)}</span>
                </div>

                {/* Obligations are never hidden behind a click — they are the terms the creator
                    takes on, and a person who does not read them can be billed or taken down. */}
                {c.obligations.length > 0 && (
                  <ul class="stack" data-connector-obligations style={{ gap: 4, margin: '0 0 10px', paddingLeft: 18 }}>
                    {c.obligations.map((o, i) => (
                      <li key={i} class="muted" style={{ fontSize: 12.5 }}>{o}</li>
                    ))}
                  </ul>
                )}

                <button
                  class="btn sm" data-connector-toggle={c.id} aria-expanded={open}
                  onClick={() => setOpenId(open ? null : c.id)}
                >{open ? 'Hide details' : 'Details'}</button>

                {open && (
                  <dl class="kv" data-connector-details={c.id} style={{ marginTop: 12, fontSize: 12.5 }}>
                    <Row k="Licence" v={c.license} />
                    <Row k="Attribution" v={c.attribution || 'None required'} />
                    <Row k="Free quota" v={c.freeQuota} />
                    <Row k="Rate limit" v={c.rateLimit} />
                    <Row k="Updates" v={c.freshness} />
                    <Row k="Cost" v={c.cost} />
                    <Row k="If unavailable" v={c.fallback} />
                    <Row k="Transport" v={c.transport} />
                    <dt class="muted">Docs</dt>
                    <dd><a href={c.docsUrl} target="_blank" rel="noopener noreferrer" data-connector-docs>{c.docsUrl}</a></dd>
                  </dl>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (<><dt class="muted">{k}</dt><dd>{v}</dd></>);
}

function formatTtl(s: number): string {
  if (s <= 0) return 'on push';
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} d`;
}
